// usePersistentState — like useState but mirrors the value to localStorage
// under a namespaced key. Used by the public-screen filters (Standings tab,
// Results tab, Teams search/filter) so that navigating away and back via
// the team-detail "back" button preserves the user's last selection.
//
// Reads the saved value at mount; writes on every change. Storage failures
// (private mode, quota exceeded) silently no-op so the hook degrades to a
// plain useState. Values must be JSON-serializable.

import { useEffect, useRef, useState } from 'react';

const KEY_PREFIX = 'lightning.filters.';

export function usePersistentState<T>(
  key: string,
  initial: T,
): [T, (next: T) => void] {
  const fullKey = KEY_PREFIX + key;
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(fullKey);
      if (raw == null) return initial;
      return JSON.parse(raw) as T;
    } catch {
      return initial;
    }
  });
  // Skip the initial write — the initial value either came from localStorage
  // (already there) or is the default (no need to persist until the user
  // actually changes it).
  const skipFirst = useRef(true);
  useEffect(() => {
    if (skipFirst.current) {
      skipFirst.current = false;
      return;
    }
    try {
      localStorage.setItem(fullKey, JSON.stringify(value));
    } catch {
      // Private mode / quota exceeded — silently degrade.
    }
  }, [fullKey, value]);
  return [value, setValue];
}
